import { useTranslation } from '../utils/i18n';
import React, { useCallback, useContext, useEffect, useRef } from 'react';

import { AppContext } from '../context/AppContext';
import { debounce } from '../utils/core';
import { getGoogleDriveStatus, syncGoogleDriveNow } from '../utils/driveSync';
import { logger } from '../utils/logger';

const SYNC_INTERVAL_MS = 15 * 60 * 1000;
const LOCAL_CHANGE_DEBOUNCE_MS = 20_000;
const FOREGROUND_RETRY_DELAY_MS = 8_000;

const shouldRetryForegroundSync = (reason: string): boolean =>
  reason === 'visibility' ||
  reason === 'focus' ||
  reason === 'pageshow' ||
  reason === 'pageshow-bfcache';

const DriveSyncRuntime: React.FC = () => {
  const t = useTranslation();
  const { localForageLoaded, user, isOnline, setSnackbarMessage, triggerRefresh } = useContext(AppContext);
  const readyRef = useRef(false);
  const connectedRef = useRef(false);
  const foregroundRetryTimerRef = useRef<number | null>(null);

  const runSync = useCallback(
    async (reason: string) => {
      if (!readyRef.current || !connectedRef.current || !isOnline) {
        return;
      }

      try {
        const manifest = await syncGoogleDriveNow(reason);
        if (foregroundRetryTimerRef.current !== null) {
          window.clearTimeout(foregroundRetryTimerRef.current);
          foregroundRetryTimerRef.current = null;
        }
        if (manifest) {
          triggerRefresh();
        }
      } catch (error) {
        logger.warn('Background Google Drive sync failed', {
          reason,
          error: error instanceof Error ? error.message : String(error),
        });
        if (
          shouldRetryForegroundSync(reason) &&
          document.visibilityState === 'visible' &&
          foregroundRetryTimerRef.current === null
        ) {
          foregroundRetryTimerRef.current = window.setTimeout(() => {
            foregroundRetryTimerRef.current = null;
            void runSync(`${reason}-retry`);
          }, FOREGROUND_RETRY_DELAY_MS);
        }
      }
    },
    [isOnline, triggerRefresh],
  );

  const debouncedLocalSyncRef = useRef(
    debounce(() => {
      void runSync('local-change');
    }, LOCAL_CHANGE_DEBOUNCE_MS),
  );

  useEffect(() => {
    readyRef.current = Boolean(localForageLoaded && user?.email);
  }, [localForageLoaded, user?.email]);

  useEffect(() => {
    if (!localForageLoaded || !user?.email || !isOnline) {
      connectedRef.current = false;
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const status = await getGoogleDriveStatus();
        if (cancelled) {
          return;
        }
        connectedRef.current = Boolean(status.configured && status.connected);
        if (connectedRef.current) {
          void runSync('startup');
        }
      } catch (error) {
        connectedRef.current = false;
        logger.debug('Google Drive status check failed', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOnline, localForageLoaded, runSync, user?.email]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const driveStatus = params.get('drive');
    if (!driveStatus) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('drive');
    window.history.replaceState({}, '', url.toString());

    if (driveStatus === 'connected') {
      setSnackbarMessage({
        text: t('DRIVE_CONNECTED'),
        duration: 4000,
      });
      connectedRef.current = true;
      void runSync('drive-connected');
    } else if (driveStatus === 'mismatch') {
      setSnackbarMessage({
        text: t('DRIVE_ACCOUNT_MISMATCH'),
        duration: 6000,
      });
    } else if (driveStatus === 'scope') {
      setSnackbarMessage({
        text: t('DRIVE_PERMISSION_REQUIRED'),
        duration: 6000,
      });
    } else {
      setSnackbarMessage({
        text: t('DRIVE_CONNECTION_FAILED'),
        duration: 5000,
      });
    }
  }, [runSync, setSnackbarMessage, t]);

  useEffect(() => {
    const handleLocalChange = (event: Event) => {
      if (!connectedRef.current) {
        return;
      }
      const detail = (event as CustomEvent<{ reason?: string }>).detail;
      if (
        detail?.reason === 'google-drive-sync-cloud-state' ||
        detail?.reason === 'google-drive-sync-cover-downloaded' ||
        detail?.reason === 'google-drive-sync-remote-apply'
      ) {
        logger.debug('Google Drive sync ignored local change from Drive sync bookkeeping', {
          reason: detail.reason,
        });
        return;
      }
      void debouncedLocalSyncRef.current();
    };

    const handleSyncComplete = () => {
      triggerRefresh();
    };

    const handleDriveConnected = () => {
      connectedRef.current = true;
      void runSync('drive-connected');
    };

    const handleSyncError = (event: Event) => {
      const detail = (event as CustomEvent<{ error?: string }>).detail;
      logger.warn('Google Drive sync error event', detail);
    };

    const runForegroundSync = (reason: string) => {
      if (document.visibilityState === 'visible') {
        void runSync(reason);
      }
    };

    const handleVisibility = () => runForegroundSync('visibility');
    const handleFocus = () => runForegroundSync('focus');
    const handlePageShow = (event: PageTransitionEvent) =>
      runForegroundSync(event.persisted ? 'pageshow-bfcache' : 'pageshow');

    const handleOnline = () => {
      void runSync('online');
    };

    window.addEventListener('wereader:local-data-changed', handleLocalChange);
    window.addEventListener('wereader:drive-connected', handleDriveConnected);
    window.addEventListener('wereader:drive-sync-complete', handleSyncComplete);
    window.addEventListener('wereader:drive-sync-error', handleSyncError);
    window.addEventListener('online', handleOnline);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibility);
    const intervalId = window.setInterval(() => void runSync('interval'), SYNC_INTERVAL_MS);

    return () => {
      window.removeEventListener('wereader:local-data-changed', handleLocalChange);
      window.removeEventListener('wereader:drive-connected', handleDriveConnected);
      window.removeEventListener('wereader:drive-sync-complete', handleSyncComplete);
      window.removeEventListener('wereader:drive-sync-error', handleSyncError);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearInterval(intervalId);
      if (foregroundRetryTimerRef.current !== null) {
        window.clearTimeout(foregroundRetryTimerRef.current);
        foregroundRetryTimerRef.current = null;
      }
      debouncedLocalSyncRef.current.cancel();
    };
  }, [runSync, triggerRefresh]);

  return null;
};

export default DriveSyncRuntime;
